/**
 * DealerPuck — floating dealer button. Detached from any avatar; slides
 * between seats (700ms ease) as the button advances between hands.
 */

export type DealerSeat = 'opp0' | 'opp1' | 'opp2' | 'opp3' | 'opp4' | 'hero';

const SEAT_POS: Readonly<Record<DealerSeat, { left: string; top: string }>> = {
  opp0: { left: '15%', top: '28%' },
  opp1: { left: '32%', top: '22%' },
  opp2: { left: '50%', top: '18%' },
  opp3: { left: '68%', top: '22%' },
  opp4: { left: '85%', top: '28%' },
  hero: { left: '50%', top: '76%' },
};

/** Narrow a numeric opponent index (0..4) into the `opp*` tagged seat. */
export function oppSeat(index: number): DealerSeat {
  const clamped = Math.max(0, Math.min(4, Math.floor(index)));
  return `opp${clamped}` as DealerSeat;
}

/**
 * Compute which seat currently holds the dealer button.
 * @param heroSeat  the hero's seat number (engine) or null if pre-hand
 * @param button    the current hand's button seat (engine) or null
 * @param orderedOpponents opponents ordered by seat (already filtered
 *        of the hero); used to map the button-seat number to an `opp*`
 *        tag via index.
 */
export function resolveDealerSeat(
  heroSeat: number | null | undefined,
  button: number | null | undefined,
  orderedOpponents: ReadonlyArray<{ seat: number }>,
): DealerSeat | null {
  if (button == null) return null;
  if (heroSeat === button) return 'hero';
  const idx = orderedOpponents.findIndex((p) => p.seat === button);
  return idx >= 0 ? oppSeat(idx) : null;
}

export type DealerPuckProps = {
  /** Current dealer seat; null = hidden (pre-hand / between-hands). */
  seat: DealerSeat | null;
};

export function DealerPuck({ seat }: DealerPuckProps) {
  const pos = seat ? SEAT_POS[seat] : null;
  return (
    <div
      className={`absolute w-8 h-8 rounded-pill bg-chip-gold text-[11px] font-display font-bold text-bg-deeper flex items-center justify-center border-2 border-bg-deeper shadow-[0_0_14px_hsl(var(--color-warning)/0.85)] z-30 -translate-x-1/2 -translate-y-1/2 ${
        pos ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{
        left: pos?.left ?? '50%',
        top: pos?.top ?? '50%',
        transition:
          'left 700ms cubic-bezier(0.22, 1, 0.36, 1), top 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms',
      }}
      data-testid="dealer-puck"
      data-seat={seat ?? undefined}
      title="딜러 버튼"
    >
      D
    </div>
  );
}
